import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Accordion,
  Form,
  Badge,
  FormCheck,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle,
  faExclamationTriangle,
  faCalendarAlt,
  faSearch,
  faSort,
  faSortUp,
  faSortDown
} from '@fortawesome/free-solid-svg-icons';

import MainLayouts from 'layouts/MainLayouts';
import CardWrapper from 'components/CardWrapper';
import AddToCartButton from 'components/AddToCartButton';
import {
  getRenewOptions,
  getAllRenewServices,
  selectHostingService,
  selectDomainService,
  selectAllRelatedDomains,
  updateHostingBillingCycle,
  updateDomainPeriod,
  clearSelections
} from 'features/renew/renewSlice';
import {
  calculateRemainingDays,
  formatIDR,
  getBillingCycleLabel,
  isServiceRenewable,
  isDomainRenewable,
  formatDate
} from 'features/renew/renewAPI';
import { notifyError, notifySuccess } from 'components/ToastMessage';
import { addToCart } from 'features/common/layouts/cartSlice';
import './style.css';

// Function to calculate next due date based on billing cycle
const calculateNextDueDate = (currentDueDate, billingCycle) => {
  const currentDate = new Date(currentDueDate);
  let nextDate = new Date(currentDate);

  switch (billingCycle.toLowerCase()) {
    case 'monthly':
      nextDate.setMonth(nextDate.getMonth() + 1);
      break;
    case 'quarterly':
      nextDate.setMonth(nextDate.getMonth() + 3);
      break;
    case 'semiannually':
      nextDate.setMonth(nextDate.getMonth() + 6);
      break;
    case 'annually':
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      break;
    case 'biennially':
      nextDate.setFullYear(nextDate.getFullYear() + 2);
      break;
    case 'triennially':
      nextDate.setFullYear(nextDate.getFullYear() + 3);
      break;
    default:
      nextDate.setFullYear(nextDate.getFullYear() + 1);
  }

  return nextDate.toLocaleDateString('id-ID');
};

const Renew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const serviceid = searchParams.get('serviceid');

  const { status_login } = useSelector((state) => state.auth);
  const { productsList: cartItems } = useSelector((state) => state.cart);
  const {
    currentService,
    hostingServices,
    domainServices,
    selectedHostingServices,
    selectedDomainServices,
    totalAmount,
    status,
    hostingServicesStatus,
    domainServicesStatus,
    error
  } = useSelector((state) => state.renew);

  const [nameFilter, setNameFilter] = useState('');
  const [hostingSort, setHostingSort] = useState('duedate'); // 'duedate', 'name', 'price'
  const [domainSort, setDomainSort] = useState('duedate'); // 'duedate', 'name', 'price'
  const [hostingSortDirection, setHostingSortDirection] = useState('asc'); // 'asc', 'desc'
  const [domainSortDirection, setDomainSortDirection] = useState('asc'); // 'asc', 'desc'
  const [activeAccordion, setActiveAccordion] = useState(['0', '1']); // Allow both sections to be open

  // Check if user is logged in
  const isAuthenticated = status_login;
  // Reload data when user logs in
  useEffect(() => {
    if (isAuthenticated) {
      if (serviceid) {
        dispatch(getRenewOptions(serviceid));
      } else {
        dispatch(getAllRenewServices());
      }
    }
  }, [isAuthenticated, dispatch, serviceid]);

  // Load data based on serviceid parameter
  useEffect(() => {
    if (serviceid) {
      dispatch(getRenewOptions(serviceid));
    } else {
      dispatch(getAllRenewServices());
    }
  }, [dispatch, serviceid]);

  // Handle hosting service selection
  const handleHostingServiceSelect = (service, checked) => {
    if (service.can_renew) {
      dispatch(selectHostingService(service));

      // If hosting is selected, auto-select related domains by domain name
      if (checked && service.domain && service.domain !== 'No Domain') {
        // Find domain in domainServices by domain name
        const matchingDomain = domainServices.find(d => d.domain === service.domain);
        if (matchingDomain && matchingDomain.can_renew) {
          // Check if domain is already selected to avoid duplicates
          const isAlreadySelected = selectedDomainServices.some(d => d.domainid === matchingDomain.domainid);
          if (!isAlreadySelected) {
            dispatch(selectDomainService(matchingDomain));
          }
        }
      }

      // Also check related_domains if available (fallback)
      if (checked && service.related_domains && service.related_domains.length > 0) {
        // Auto-select all renewable related domains
        service.related_domains.forEach(domain => {
          if (domain.can_renew) {
            // Check if domain is already selected to avoid duplicates
            const isAlreadySelected = selectedDomainServices.some(d => d.domainid === domain.domainid);
            if (!isAlreadySelected) {
              dispatch(selectDomainService(domain));
            }
          }
        });
      }
    }
  };

  // Handle domain service selection
  const handleDomainServiceSelect = (domain, checked) => {
    // Check if this is a bundled domain using backend flag
    const isBundleHostingDomain = domain.isBundleHostingDomain === true;

    // console.log('🔍 Domain bundle check for:', domain.domain);
    // console.log('🔍 Domain isBundleHostingDomain (backend):', domain.isBundleHostingDomain);
    // console.log('🔍 Domain amount:', domain.amount);
    // console.log('🔍 Domain renew_price:', domain.renew_price);
    // console.log('🔍 Domain pricing_options:', domain.pricing_options);
    // console.log('🔍 Related hosting service:', domain.relatedHostingService);
    // console.log('🔍 Is bundle hosting domain (frontend):', isBundleHostingDomain);

    // Domain should be selectable if it can renew OR if it's a bundled domain
    const isSelectable = domain.can_renew || isBundleHostingDomain;

    if (isSelectable) {
      dispatch(selectDomainService(domain));
    }
  };

  // Handle select all related domains for hosting service
  const handleSelectAllRelatedDomains = (hostingService) => {
    // Select all renewable related domains that are not already selected
    hostingService.related_domains.forEach(domain => {
      if (domain.can_renew) {
        const isAlreadySelected = selectedDomainServices.some(d => d.domainid === domain.domainid);
        if (!isAlreadySelected) {
          dispatch(selectDomainService(domain));
        }
      }
    });
  };

  // Handle billing cycle change for hosting service
  const handleBillingCycleChange = (serviceid, billingcycle, price) => {
    dispatch(updateHostingBillingCycle({ serviceid, billingcycle, price }));
  };

  // Handle period change for domain service
  const handlePeriodChange = (domainid, period, price) => {
    dispatch(updateDomainPeriod({ domainid, period, price }));
  };

  // Filter and sort functions
  const getFilteredAndSortedHostingServices = () => {
    // Create a copy of the array to avoid modifying the original
    let filtered = [...hostingServices];

    // Filter by name if filter is set
    if (nameFilter) {
      filtered = filtered.filter(service =>
        (service.productname && service.productname.toLowerCase().includes(nameFilter.toLowerCase())) ||
        (service.domain && service.domain.toLowerCase().includes(nameFilter.toLowerCase()))
      );
    }

    // Sort services
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (hostingSort) {
        case 'name':
          aValue = a.productname.toLowerCase();
          bValue = b.productname.toLowerCase();
          break;
        case 'price':
          aValue = parseFloat(a.renew_price);
          bValue = parseFloat(b.renew_price);
          break;
        case 'duedate':
        default:
          aValue = new Date(a.nextduedate);
          bValue = new Date(b.nextduedate);
          break;
      }

      if (hostingSortDirection === 'desc') {
        return aValue < bValue ? 1 : -1;
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const getFilteredAndSortedDomainServices = () => {
    // Create a copy of the array to avoid modifying the original
    let filtered = [...domainServices];

    // Filter by name if filter is set
    if (nameFilter) {
      filtered = filtered.filter(domain =>
        domain.domain && domain.domain.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    // Sort domains
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (domainSort) {
        case 'name':
          aValue = a.domain.toLowerCase();
          bValue = b.domain.toLowerCase();
          break;
        case 'price':
          aValue = parseFloat(a.renew_price);
          bValue = parseFloat(b.renew_price);
          break;
        case 'duedate':
        default:
          aValue = new Date(a.nextduedate);
          bValue = new Date(b.nextduedate);
          break;
      }

      if (domainSortDirection === 'desc') {
        return aValue < bValue ? 1 : -1;
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const handleSort = (type, field) => {
    if (type === 'hosting') {
      if (hostingSort === field) {
        setHostingSortDirection(hostingSortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setHostingSort(field);
        setHostingSortDirection('asc');
      }
    } else {
      if (domainSort === field) {
        setDomainSortDirection(domainSortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setDomainSort(field);
        setDomainSortDirection('asc');
      }
    }
  };

  const getSortIcon = (type, field) => {
    const currentSort = type === 'hosting' ? hostingSort : domainSort;
    const currentDirection = type === 'hosting' ? hostingSortDirection : domainSortDirection;

    if (currentSort === field) {
      return currentDirection === 'asc' ? faSortUp : faSortDown;
    }
    return faSort;
  };

  // Handle add to cart
  const handleAddToCart = () => {
    const allRenewals = [
      // Include current service if serviceid is provided
      ...(serviceid && currentService && currentService.can_renew ? [{
        serviceid: currentService.serviceid,
        type: 'hosting',
        price: parseFloat(currentService.selectedPrice || currentService.amount || currentService.renew_price || 0),
        billingcycle: currentService.selectedBillingCycle || currentService.billingcycle,
        service: currentService
      }] : []),
      ...selectedHostingServices.map(service => ({
        serviceid: service.serviceid,
        type: 'hosting',
        price: parseFloat(service.selectedPrice || service.amount || service.renew_price || 0),
        billingcycle: service.selectedBillingCycle || service.billingcycle,
        service: service
      })),
      ...selectedDomainServices.map(domain => {
        // For bundled domains, calculate correct price based on hosting selection
        let domainPrice = 0;
        const isBundleHostingDomain = domain.isBundleHostingDomain === true;

        if (isBundleHostingDomain) {
          // Check if hosting is selected
          const isRelatedHostingSelected = hostingServices.some(hosting =>
            hosting.domain === domain.domain &&
            selectedHostingServices.some(selected => selected.serviceid === hosting.serviceid)
          );

          if (isRelatedHostingSelected) {
            // Hosting is selected, check if selected period is within free periods
            const selectedPeriod = domain.selectedPeriod || domain.regperiod || 1;
            const freePeriods = Math.min(domain.freePeriods || 1, 1); // Maksimal 1 tahun gratis
            
            if (selectedPeriod <= freePeriods) {
              // Period is within free range (maksimal 1 tahun)
              domainPrice = 0;
              // console.log('✅ Domain bundled with hosting - Hosting selected, period', selectedPeriod, 'is FREE (within', freePeriods, 'free periods, max 1 tahun)');
            } else {
              // Period exceeds free range, use normal pricing
              // console.log('💰 Domain bundled with hosting - Hosting selected, but period', selectedPeriod, 'exceeds free periods ( ', freePeriods, '), using normal pricing');
              if (domain.selectedPeriod) {
                const selectedOption = domain.pricing_options.find(opt => opt.period == domain.selectedPeriod);
                domainPrice = selectedOption ? parseFloat(selectedOption.amount) : 0;
              } else {
                domainPrice = domain.pricing_options[0] ? parseFloat(domain.pricing_options[0].amount) : 0;
              }
            }
          } else {
            // Hosting is not selected, domain uses pricing from pricing_options
            if (domain.selectedPeriod) {
              const selectedOption = domain.pricing_options.find(opt => opt.period == domain.selectedPeriod);
              domainPrice = selectedOption ? parseFloat(selectedOption.amount) : 0;
            } else {
              domainPrice = domain.pricing_options[0] ? parseFloat(domain.pricing_options[0].amount) : 0;
            }
            // console.log('💰 Domain bundled with hosting - Hosting not selected, using normal pricing:', domainPrice);
          }
        } else {
          // Not a bundled domain, use normal pricing
          domainPrice = parseFloat(domain.selectedPrice || domain.amount || domain.renew_price || 0);
          // console.log('💰 Domain not bundled - Using normal pricing:', domainPrice);
        }

        // Debug logging for domain period
        const finalPeriod = domain.selectedPeriod || domain.regperiod || 1;
        // console.log('🔍 Domain renewal data for', domain.domain, ':', {
        //   domainid: domain.domainid,
        //   selectedPeriod: domain.selectedPeriod,
        //   regperiod: domain.regperiod,
        //   finalPeriod: finalPeriod,
        //   price: domainPrice,
        //   pricing_options: domain.pricing_options
        // });

        return {
          domainid: domain.domainid,
          type: 'domain',
          price: domainPrice,
          period: finalPeriod,
          domain: domain
        };
      })
    ];

    // console.log('📋 All renewals to be processed:', allRenewals);
    // console.log('Current service:', currentService);
    // console.log('Selected hosting services:', selectedHostingServices);
    // console.log('Selected domain services:', selectedDomainServices);

    if (allRenewals.length === 0) {
      notifyError('Silakan pilih layanan yang akan diperpanjang');
      return;
    }

    // Add each renewal to cart
    allRenewals.forEach(renewal => {
      if (renewal.type === 'hosting') {
        const service = renewal.service;

        const cartItem = {
          type: 'renew',
          serviceid: renewal.serviceid,
          renewType: 'hosting',
          name: service.productname,
          price: renewal.price,
          domain: service.domain || '',
          billingcycle: renewal.billingcycle,
          selectedBillingCycle: renewal.billingcycle,
          // Add fields required for promo validation
          pid: service.pid || null,
          cycles: renewal.billingcycle?.toLowerCase() || 'annually',
          periods: '1Years' // Default for hosting
        };

        // console.log('🛒 Adding hosting renew to cart:', cartItem);
        dispatch(addToCart(cartItem));
      } else {
        const domain = renewal.domain;
        const cartItem = {
          type: 'renew',
          serviceid: renewal.domainid,
          renewType: 'domain',
          name: domain.domain,
          price: renewal.price,
          period: renewal.period,
          domain: domain.domain,
          selectedPeriod: renewal.period,
          billingcycle: 'annually',
          // Add fields required for promo validation
          pid: null, // Domain renewals don't have product ID
          cycles: 'annually', // Default for domain renewals
          periods: `${renewal.period || 1}Years` // Convert period to format expected by promo validation
        };

        // console.log('🛒 Adding domain renew to cart:', cartItem);
        dispatch(addToCart(cartItem));
      }
    });

    // console.log('All renewals to be added:', allRenewals);
    notifySuccess(`${allRenewals.length} layanan berhasil ditambahkan ke keranjang`);

    // Don't clear selections - keep checkboxes checked to show what's in cart
    // dispatch(clearSelections()); // Commented out to keep selections visible
  };

  // Render service status badge
  const renderStatusBadge = (status, canRenew) => {
    if (canRenew) {
      return <Badge bg="success">Tersedia</Badge>;
    } else {
      return <Badge bg="danger">Tidak Tersedia</Badge>;
    }
  };

  // Render service message based on status
  const renderServiceMessage = (status, canRenew, renewMessage) => {
    if (canRenew) {
      return null;
    }

    // For hosting services
    if (status === 'Pending Transfer' || status === 'Terminated') {
      return <small className="text-danger">Hubungi Support Kami</small>;
    }

    if (status === 'Suspended') {
      return <small className="text-danger">Hubungi Support Kami</small>;
    }

    // For domain services
    if (['Pending', 'Pending Transfer', 'Transferred Away', 'Fraud'].includes(status)) {
      return <small className="text-danger">Hubungi Support Kami</small>;
    }

    if (status === 'Redemption' || status === 'Grace') {
      return <small className="text-danger">Hubungi Support Kami</small>;
    }

    // Fallback to API message
    return <small className="text-danger">{renewMessage}</small>;
  };

  // Render hosting service card (compact version)
  const renderHostingServiceCard = (service) => {
    const remainingDays = calculateRemainingDays(service.nextduedate);
    const isSelected = selectedHostingServices.some(s => s.serviceid === service.serviceid);
    const isInCart = cartItems.some(item => item.type === 'renew' && item.serviceid === service.serviceid);
    const selectedService = isSelected ? selectedHostingServices.find(s => s.serviceid === service.serviceid) : null;

    return (
      <Card
        key={service.serviceid}
        className={`mb-3 service-card ${isSelected || isInCart ? 'border-warning' : 'border-light'}`}
        style={{ borderColor: (isSelected || isInCart) ? '#ee4d30' : undefined }}
      >
        <Card.Body className="p-3">
          <div className="card-content-wrapper">
            {/* Checkbox and Service Name */}
            <div className="checkbox-section">
              <FormCheck
                type="checkbox"
                checked={isSelected || isInCart}
                disabled={!service.can_renew}
                onChange={(e) => handleHostingServiceSelect(service, e.target.checked)}
              />
              <div className="service-name d-md-none">{service.productname}</div>
            </div>

            {/* Service Info */}
            <div className="service-info-section">
              <div className="mb-2 d-none d-md-block">
                <span className="service-type-label">Produk:</span>
                <div className="service-name">
                  {service.productname}
                  {service.hasBundledDomains && (
                    <Badge bg="success" className="ms-2" size="sm">
                      +{service.bundledDomainCount} Domain
                    </Badge>
                  )}
                  {service.hasBundledDomains && (
                    <small className="text-success d-block mt-1">
                      Domain terkait gratis untuk 1 tahun pertama
                    </small>
                  )}
                </div>
              </div>
              <div className="service-details-wrapper">
                <div className="service-details text-muted">
                  <span className="detail-label">Domain:</span>
                  <span>{service.domain || 'No Domain'}</span>
                </div>
                <div className="service-details text-muted">
                  <span className="detail-label">Jatuh Tempo:</span>
                  {formatDate(service.nextduedate)}
                  <span className="ms-1 text-warning">({remainingDays}d)</span>
                </div>
                <div className="service-details text-muted">
                  <span className="detail-label">Termin:</span>
                  {getBillingCycleLabel(service.billingcycle)}
                </div>
              </div>
            </div>

            {/* Billing Cycle Selection */}
            <div className="billing-cycle-section">
              {isSelected && service.pricing_options && service.pricing_options.length > 0 && (
                <div className="billing-cycle-dropdown">
                  <Form.Select
                    size="sm"
                    value={selectedService?.selectedBillingCycle || service.billingcycle}
                    onChange={(e) => {
                      e.stopPropagation();
                      const selectedOption = service.pricing_options.find(opt => opt.billingcycle === e.target.value);
                      if (selectedOption) {
                        handleBillingCycleChange(service.serviceid, e.target.value, selectedOption.amount);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {service.pricing_options.map(option => (
                      <option key={option.billingcycle} value={option.billingcycle}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}
            </div>

            {/* Status and Price */}
            <div className="price-section">
              <div className="status-badge">
                {renderStatusBadge(service.status, service.can_renew)}
              </div>
              <div className="price-amount">
                {formatIDR(selectedService?.selectedPrice || service.amount || service.renew_price)}
              </div>
              {!service.can_renew && (
                <small className="text-danger d-block">
                  {renderServiceMessage(service.status, service.can_renew, service.renew_message)}
                </small>
              )}
            </div>
          </div>

          {/* Related domains for hosting services */}
          {service.related_domains && service.related_domains.length > 0 && (
            <div className="mt-3 pt-3 border-top">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">
                  <span className="detail-label">Domain Terkait:</span>
                  ({service.related_domains.length})
                </small>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Select all renewable related domains that are not already selected
                    service.related_domains.forEach(domain => {
                      if (domain.can_renew) {
                        const isAlreadySelected = selectedDomainServices.some(d => d.domainid === domain.domainid);
                        if (!isAlreadySelected) {
                          dispatch(selectDomainService(domain));
                        }
                      }
                    });
                  }}
                >
                  Pilih Semua
                </Button>
              </div>
              <Row>
                {service.related_domains.map(domain => {
                  const isDomainSelected = selectedDomainServices.some(d => d.domainid === domain.domainid);
                  return (
                    <Col md={6} key={domain.domainid}>
                      <div className="d-flex justify-content-between align-items-center p-2 border rounded mb-2">
                        <div className="d-flex align-items-center flex-grow-1 me-2">
                          <FormCheck
                            type="checkbox"
                            checked={isDomainSelected || cartItems.some(item => item.type === 'renew' && item.serviceid === domain.domainid)}
                            disabled={!domain.can_renew}
                            onChange={(e) => handleDomainServiceSelect(domain, e.target.checked)}
                            className="me-2"
                          />
                          <div className="flex-grow-1">
                            <small className="fw-bold text-truncate d-block">{domain.domain}</small>
                            <small className="text-muted">
                              {formatDate(domain.nextduedate)}
                            </small>
                          </div>
                        </div>
                        <div className="text-end">
                          <small>{formatIDR(domain.amount || domain.renew_price)}</small>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  // Render domain service card (compact version)
  const renderDomainServiceCard = (domain) => {
    const remainingDays = calculateRemainingDays(domain.nextduedate);
    const isSelected = selectedDomainServices.some(d => d.domainid === domain.domainid);
    const isInCart = cartItems.some(item => item.type === 'renew' && item.serviceid === domain.domainid);
    const selectedDomain = isSelected ? selectedDomainServices.find(d => d.domainid === domain.domainid) : null;

    return (
      <Card
        key={domain.domainid}
        className={`mb-3 service-card ${isSelected || isInCart ? 'border-warning' : 'border-light'}`}
        style={{ borderColor: (isSelected || isInCart) ? '#ee4d30' : undefined }}
      >
        <Card.Body className="p-3">
          <div className="card-content-wrapper">
            {/* Checkbox and Domain Name */}
            <div className="checkbox-section">
              <FormCheck
                type="checkbox"
                checked={isSelected || isInCart}
                disabled={!domain.can_renew}
                onChange={(e) => handleDomainServiceSelect(domain, e.target.checked)}
              />
              <div className="service-name d-md-none">{domain.domain}</div>
            </div>

            {/* Domain Info */}
            <div className="service-info-section">
              <div className="mb-2 d-none d-md-block">
                <span className="service-type-label">Domain:</span>
                <div className="service-name">
                  {domain.domain}
                  {domain.isBundleHostingDomain && (
                    <Badge bg="info" className="ms-2" size="sm">
                      Terikat Hosting
                    </Badge>
                  )}
                </div>
              </div>
              <div className="service-details-wrapper">
                <div className="service-details text-muted">
                  <span className="detail-label">Jatuh Tempo:</span>
                  {formatDate(domain.nextduedate)}
                  <span className="ms-1 text-warning">({remainingDays}d)</span>
                </div>
                <div className="service-details text-muted">
                  <span className="detail-label">Periode:</span>
                  {domain.regperiod || 1} tahun
                </div>
                {domain.isBundleHostingDomain && domain.relatedHostingService && (
                  <div className="service-details text-info">
                    <span className="detail-label">Hosting Terkait:</span>
                    {domain.relatedHostingService.productname} ({domain.relatedHostingService.billingcycle})
                  </div>
                )}
                {domain.isBundleHostingDomain && domain.freePeriods > 0 && (
                  <div className="service-details text-success">
                    <span className="detail-label">Periode Gratis:</span>
                    {domain.freePeriods} tahun pertama
                  </div>
                )}
              </div>
            </div>

            {/* Period Selection */}
            <div className="billing-cycle-section">
              {isSelected && domain.pricing_options && domain.pricing_options.length > 0 && (
                <div className="billing-cycle-dropdown">
                  <Form.Select
                    size="sm"
                    value={selectedDomain?.selectedPeriod || domain.regperiod || 1}
                    onChange={(e) => {
                      e.stopPropagation();
                      const selectedOption = domain.pricing_options.find(opt => opt.period == e.target.value);
                      if (selectedOption) {
                        handlePeriodChange(domain.domainid, e.target.value, selectedOption.amount);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {domain.pricing_options.map(option => (
                      <option key={option.period} value={option.period}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              )}
            </div>

            {/* Status and Price */}
            <div className="price-section">
              <div className="status-badge">
                {renderStatusBadge(domain.status, domain.can_renew)}
              </div>
              <div className="price-amount">
                {formatIDR((() => {
                  // console.log('💰 Price calculation for domain:', domain.domain);
                  // console.log('💰 Is bundle hosting domain:', domain.isBundleHostingDomain);
                  // console.log('💰 Selected domain:', selectedDomain);

                  // For bundled domains, check if related hosting is selected
                  if (domain.isBundleHostingDomain === true) {
                    // Check if hosting is selected
                    const isRelatedHostingSelected = hostingServices.some(hosting =>
                      hosting.domain === domain.domain &&
                      selectedHostingServices.some(selected => selected.serviceid === hosting.serviceid)
                    );

                    if (isRelatedHostingSelected) {
                      // Hosting is selected, check if selected period is within free periods
                      const selectedPeriod = selectedDomain?.selectedPeriod || domain.regperiod || 1;
                      const freePeriods = Math.min(domain.freePeriods || 1, 1); // Maksimal 1 tahun gratis
                      
                      if (selectedPeriod <= freePeriods) {
                        // Period is within free range (maksimal 1 tahun)
                        // console.log('✅ Hosting selected, period', selectedPeriod, 'is FREE (within', freePeriods, 'free periods, max 1 tahun)');
                        return 0;
                      } else {
                        // Period exceeds free range, use normal pricing
                        // console.log('💰 Hosting selected, but period', selectedPeriod, 'exceeds free periods ( ', freePeriods, '), using normal pricing');
                        const selectedOption = domain.pricing_options.find(opt => opt.period == selectedPeriod);
                        const price = selectedOption ? selectedOption.amount : 0;
                        return price;
                      }
                    } else {
                      // Hosting is not selected, domain uses pricing from pricing_options
                      // console.log('💰 Hosting not selected, using normal pricing');
                      if (selectedDomain?.selectedPeriod) {
                        // Find the selected period price
                        const selectedOption = domain.pricing_options.find(opt => opt.period == selectedDomain.selectedPeriod);
                        const price = selectedOption ? selectedOption.amount : 0;
                        // console.log('💰 Selected period price:', price);
                        return price;
                      } else {
                        // Default to first period price
                        const price = domain.pricing_options[0] ? domain.pricing_options[0].amount : 0;
                        // console.log('💰 Default period price:', price);
                        return price;
                      }
                    }
                  } else {
                    // Not a bundled domain, use normal pricing
                    // console.log('💰 Not bundled domain, using normal pricing');
                    const price = selectedDomain?.selectedPrice || domain.amount || domain.renew_price;
                    // console.log('💰 Normal price:', price);
                    return price;
                  }
                })())}
              </div>
              {!domain.can_renew && (
                <small className="text-danger d-block">
                  {renderServiceMessage(domain.status, domain.can_renew, domain.renew_message)}
                </small>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // Loading state
  if (status === 'loading' || hostingServicesStatus === 'loading' || domainServicesStatus === 'loading') {
    return (
      <MainLayouts>
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Memuat data layanan...</p>
        </div>
      </MainLayouts>
    );
  }

  // Error state sudah di-handle di atas dengan tampilan yang lebih baik

  // Authentication dan error handling sudah di-handle di bawah dengan logic yang lebih baik

  // Show error message for client services (when API returns error)
  if (error && (hostingServicesStatus === "error" || domainServicesStatus === "error")) {
    // Show error notification
    notifyError("Silahkan Login terlebih dahulu");
    
    return (
      <MainLayouts>
        <div className="container py-4">
          <div className="row">
            <div className="col-12">
              {/* Main Content */}
              <CardWrapper title="Perpanjangan Layanan">
                <div className="text-center py-5">
                  <div className="mb-4">
                    <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                      <FontAwesomeIcon icon={faCalendarAlt} size="2x" className="text-muted" />
                    </div>
                    <h5 className="text-muted">Login Diperlukan</h5>
                    <p className="text-muted">Untuk mengakses fitur Renew silahkan Login terlebih dahulu...</p>
                  </div>
                </div>
              </CardWrapper>
            </div>
          </div>
        </div>
      </MainLayouts>
    );
  }

  return (
    <MainLayouts>
      <div className="container py-4">
        <div className="row">
          <div className="col-12">
            {/* Specific service view (when serviceid is provided) */}
            {serviceid && currentService ? (
              <div>
                <CardWrapper title="Informasi Layanan Saat Ini">
                  <Row>
                    <Col md={8}>
                      <div className="d-flex align-items-center mb-2">
                        <FormCheck
                          type="checkbox"
                          checked={selectedHostingServices.some(s => s.serviceid === currentService.serviceid) ||
                            cartItems.some(item => item.type === 'renew' && item.serviceid === currentService.serviceid)}
                          disabled={!currentService.can_renew}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const updatedService = {
                                ...currentService,
                                selectedBillingCycle: currentService.billingcycle,
                                selectedPrice: currentService.amount || currentService.renew_price
                              };
                              handleHostingServiceSelect(updatedService, true);
                            } else {
                              handleHostingServiceSelect(currentService, false);
                            }
                          }}
                          className="me-2"
                        />
                        <h5 className="mb-0">{currentService.productname}</h5>
                      </div>
                      <p className="text-muted">
                        <span className="detail-label">Domain:</span>
                        {currentService.domain || 'No Domain'}
                      </p>
                      <p className="text-muted">
                        <span className="detail-label">Jatuh Tempo:</span>
                        {new Date(currentService.nextduedate).toLocaleDateString('id-ID')}
                      </p>
                      <p className="text-muted">
                        <span className="detail-label">Termin:</span>
                        {getBillingCycleLabel(currentService.billingcycle)}
                      </p>

                      {/* Billing Cycle Selection for current service */}
                      {currentService.pricing_options && currentService.pricing_options.length > 0 && (
                        <div className="mt-3">
                          <label className="form-label small">Pilih Termin Pembayaran:</label>
                          <Form.Select
                            size="sm"
                            value={currentService.billingcycle}
                            onChange={(e) => {
                              const selectedOption = currentService.pricing_options.find(opt => opt.billingcycle === e.target.value);
                              if (selectedOption) {
                                // Update current service with new billing cycle
                                const updatedService = {
                                  ...currentService,
                                  selectedBillingCycle: e.target.value,
                                  selectedPrice: selectedOption.amount
                                };
                                handleHostingServiceSelect(updatedService);
                              }
                            }}
                          >
                            {currentService.pricing_options.map(option => (
                              <option key={option.billingcycle} value={option.billingcycle}>
                                {option.label} - {formatIDR(option.amount)}
                              </option>
                            ))}
                          </Form.Select>
                        </div>
                      )}
                    </Col>
                    <Col md={4} className="text-end">
                      <div className="mb-2">
                        {renderStatusBadge(currentService.status, currentService.can_renew)}
                      </div>
                      <div className="mb-2">
                        <strong className="text-primary">
                          {formatIDR(currentService.selectedPrice || currentService.amount || currentService.renew_price)}
                        </strong>
                      </div>
                      {!currentService.can_renew && renderServiceMessage(currentService.status, currentService.can_renew, currentService.renew_message)}
                    </Col>
                  </Row>
                </CardWrapper>

                {/* Related domains for hosting service */}
                {currentService.related_domains && currentService.related_domains.length > 0 && (
                  <CardWrapper title="Domain Terkait" className="mt-4">
                    <div className="mb-3">
                      <Button
                        variant="outline-primary"
                        onClick={() => {
                          // Select all renewable related domains that are not already selected
                          currentService.related_domains.forEach(domain => {
                            if (domain.can_renew) {
                              const isAlreadySelected = selectedDomainServices.some(d => d.domainid === domain.domainid);
                              if (!isAlreadySelected) {
                                dispatch(selectDomainService(domain));
                              }
                            }
                          });
                        }}
                      >
                        Pilih Semua Domain
                      </Button>
                    </div>
                    {currentService.related_domains.map(domain => {
                      const isDomainSelected = selectedDomainServices.some(d => d.domainid === domain.domainid);
                      return (
                        <div
                          key={domain.domainid}
                          className="d-flex justify-content-between align-items-center p-2 border rounded mb-2"
                        >
                          <div className="d-flex align-items-center">
                            <FormCheck
                              type="checkbox"
                              checked={isDomainSelected || cartItems.some(item => item.type === 'renew' && item.serviceid === domain.domainid)}
                              disabled={!domain.can_renew}
                              onChange={(e) => handleDomainServiceSelect(domain, e.target.checked)}
                              className="me-2"
                            />
                            <div>
                              <strong>{domain.domain}</strong>
                              <br />
                              <small className="text-muted">
                                <span className="detail-label">Jatuh Tempo:</span>
                                {formatDate(domain.nextduedate)}
                              </small>
                              {!domain.can_renew && (
                                <>
                                  <br />
                                  <small className="text-danger">
                                    {renderServiceMessage(domain.status, domain.can_renew, domain.renew_message)}
                                  </small>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-end">
                            <div className="mb-1">
                              {renderStatusBadge(domain.status, domain.can_renew)}
                            </div>
                            <div>
                              <strong>{formatIDR(domain.amount || domain.renew_price)}</strong>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </CardWrapper>
                )}
              </div>
            ) : (
              /* All services view */
              <div>
                <CardWrapper title="Perpanjangan Layanan" className="mb-4">
                  <Alert variant="warning" className="mb-3">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                    Jika anda tidak menemukan layanan yang ingin anda perpanjang, hubungi Support Kami
                  </Alert>

                  {/* Filter Controls */}
                  <div className="filter-section">
                    <Row>
                      <Col md={12}>
                        <InputGroup>
                          <InputGroup.Text>
                            <FontAwesomeIcon icon={faSearch} />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Cari berdasarkan nama produk atau domain..."
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                          />
                        </InputGroup>
                      </Col>
                    </Row>
                  </div>
                  {/* Accordion Control Buttons */}
                  {/* <div className="accordion-controls">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => setActiveAccordion(['0', '1'])}
                    >
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                      Buka Semua
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setActiveAccordion([])}
                    >
                      <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                      Tutup Semua
                    </Button>
                  </div> */}

                  <Accordion activeKey={activeAccordion} onSelect={(k) => {
                    // Handle multiple accordion selection
                    if (Array.isArray(activeAccordion)) {
                      if (activeAccordion.includes(k)) {
                        // Remove from active keys
                        setActiveAccordion(activeAccordion.filter(key => key !== k));
                      } else {
                        // Add to active keys
                        setActiveAccordion([...activeAccordion, k]);
                      }
                    } else {
                      // Fallback for single selection
                      setActiveAccordion(k);
                    }
                  }}>
                    {/* Hosting Services */}
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        Layanan Hosting ({getFilteredAndSortedHostingServices().length})
                      </Accordion.Header>
                      <Accordion.Body>
                        {hostingServices.length > 0 ? (
                          <>
                            {/* Sort Controls for Hosting */}
                            <div className="mb-3">
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleSort('hosting', 'duedate')}
                                >
                                  <FontAwesomeIcon icon={getSortIcon('hosting', 'duedate')} className="me-1" />
                                  Due Date
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleSort('hosting', 'name')}
                                >
                                  <FontAwesomeIcon icon={getSortIcon('hosting', 'name')} className="me-1" />
                                  Nama
                                </Button>
                              </div>
                            </div>

                            {/* Hosting Services in single column */}
                            <div>
                              {getFilteredAndSortedHostingServices().map(service => (
                                <div key={service.serviceid}>
                                  {renderHostingServiceCard(service)}
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-muted text-center py-3">
                            Tidak ada layanan hosting yang dapat diperpanjang
                          </p>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>

                    {/* Domain Services */}
                    <Accordion.Item eventKey="1">
                      <Accordion.Header>
                        Layanan Domain ({getFilteredAndSortedDomainServices().length})
                      </Accordion.Header>
                      <Accordion.Body>
                        {domainServices.length > 0 ? (
                          <>
                            {/* Sort Controls for Domain */}
                            <div className="mb-3">
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleSort('domain', 'duedate')}
                                >
                                  <FontAwesomeIcon icon={getSortIcon('domain', 'duedate')} className="me-1" />
                                  Due Date
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleSort('domain', 'name')}
                                >
                                  <FontAwesomeIcon icon={getSortIcon('domain', 'name')} className="me-1" />
                                  Nama Domain
                                </Button>
                              </div>
                            </div>

                            {/* Domain Services in single column */}
                            <div>
                              {getFilteredAndSortedDomainServices().map(domain => (
                                <div key={domain.domainid}>
                                  {renderDomainServiceCard(domain)}
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <p className="text-muted text-center py-3">
                            Tidak ada layanan domain yang dapat diperpanjang
                          </p>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </CardWrapper>
              </div>
            )}

            {/* Summary and Checkout */}
            {((serviceid && currentService && currentService.can_renew) || selectedHostingServices.length > 0 || selectedDomainServices.length > 0) && (
              <CardWrapper title="Ringkasan Perpanjangan" className="mt-3">
                <Row>
                  <Col md={12}>
                    <h6 className="mb-2">Ringkasan Layanan:</h6>

                    {/* Current service if serviceid is provided */}
                    {serviceid && currentService && currentService.can_renew && (
                      <div className="d-flex justify-content-between align-items-center py-1 mb-1">
                        <div className="flex-grow-1">
                          <span className="service-type-label me-2">Hosting:</span>
                          <span className="small">{currentService.productname}</span>
                          <br />
                          <small className="text-muted">
                            Termin: {getBillingCycleLabel(currentService.selectedBillingCycle || currentService.billingcycle)}
                            </small>
                        </div>
                        <div className="text-end" style={{ minWidth: '120px' }}>
                          <span className="fw-bold">{formatIDR(currentService.selectedPrice || currentService.amount || currentService.renew_price || 0)}</span>
                        </div>
                      </div>
                    )}

                    {/* Selected hosting services */}
                    {selectedHostingServices.map(service => (
                      <div key={service.serviceid} className="d-flex justify-content-between align-items-center py-1 mb-1">
                        <div className="flex-grow-1">
                          <span className="service-type-label me-2">Hosting:</span>
                          <span className="small">{service.productname}</span>
                          <br />
                          <small className="text-muted">
                            Termin: {getBillingCycleLabel(service.selectedBillingCycle || service.billingcycle)}
                            </small>
                        </div>
                        <div className="text-end" style={{ minWidth: '120px' }}>
                          <span className="fw-bold">{formatIDR(service.selectedPrice || service.amount || service.renew_price || 0)}</span>
                        </div>
                      </div>
                    ))}

                    {/* Selected domain services */}
                    {selectedDomainServices.map(domain => {
                      const domainPricing = (() => {
                          const isBundleHostingDomain = domain.isBundleHostingDomain === true;

                          if (isBundleHostingDomain) {
                            const isRelatedHostingSelected = hostingServices.some(hosting =>
                              hosting.domain === domain.domain &&
                              selectedHostingServices.some(selected => selected.serviceid === hosting.serviceid)
                            );

                            if (isRelatedHostingSelected) {
                              const selectedPeriod = domain.selectedPeriod || domain.regperiod || 1;
                              const freePeriods = domain.freePeriods || 1;
                              
                              if (selectedPeriod <= freePeriods) {
                              let originalPrice = 0;
                              if (domain.selectedPeriod) {
                                const selectedOption = domain.pricing_options.find(opt => opt.period == domain.selectedPeriod);
                                originalPrice = selectedOption ? parseFloat(selectedOption.amount) : 0;
                              } else {
                                const currentPeriodOption = domain.pricing_options.find(opt => opt.period == (domain.regperiod || 1));
                                originalPrice = currentPeriodOption ? parseFloat(currentPeriodOption.amount) : 0;
                              }

                              return {
                                finalPrice: 0,
                                originalPrice: originalPrice,
                                discount: originalPrice,
                                isDiscounted: true,
                                discountReason: `Gratis ${freePeriods} tahun pertama`
                              };
                            } else {
                                if (domain.selectedPeriod) {
                                  const selectedOption = domain.pricing_options.find(opt => opt.period == domain.selectedPeriod);
                                return {
                                  finalPrice: selectedOption ? parseFloat(selectedOption.amount) : 0,
                                  originalPrice: selectedOption ? parseFloat(selectedOption.amount) : 0,
                                  discount: 0,
                                  isDiscounted: false
                                };
                                } else {
                                const currentPeriodOption = domain.pricing_options.find(opt => opt.period == (domain.regperiod || 1));
                                return {
                                  finalPrice: currentPeriodOption ? parseFloat(currentPeriodOption.amount) : 0,
                                  originalPrice: currentPeriodOption ? parseFloat(currentPeriodOption.amount) : 0,
                                  discount: 0,
                                  isDiscounted: false
                                };
                                }
                              }
                            } else {
                              if (domain.selectedPeriod) {
                                const selectedOption = domain.pricing_options.find(opt => opt.period == domain.selectedPeriod);
                              return {
                                finalPrice: selectedOption ? parseFloat(selectedOption.amount) : 0,
                                originalPrice: selectedOption ? parseFloat(selectedOption.amount) : 0,
                                discount: 0,
                                isDiscounted: false
                              };
                              } else {
                              const price = domain.pricing_options[0] ? parseFloat(domain.pricing_options[0].amount) : 0;
                              return {
                                finalPrice: price,
                                originalPrice: price,
                                discount: 0,
                                isDiscounted: false
                              };
                              }
                            }
                          } else {
                          const price = domain.selectedPrice || domain.amount || domain.renew_price;
                          return {
                            finalPrice: parseFloat(price || 0),
                            originalPrice: parseFloat(price || 0),
                            discount: 0,
                            isDiscounted: false
                          };
                        }
                      })();

                      return (
                        <div key={domain.domainid} className="d-flex justify-content-between align-items-center py-1 mb-1">
                          <div className="flex-grow-1">
                            <span className="service-type-label me-2">Domain:</span>
                            <span className="small">{domain.domain}</span>
                            <br />
                            <small className="text-muted">
                              Periode: {domain.selectedPeriod || domain.regperiod || 1} tahun
                            </small>
                      </div>
                          <div className="text-end" style={{ minWidth: '120px' }}>
                            {domainPricing.isDiscounted ? (
                              <div>
                                <div className="text-decoration-line-through text-muted small">
                                  {formatIDR(domainPricing.originalPrice)}
                                </div>
                                <div className="text-success fw-bold">
                                  = {formatIDR(domainPricing.finalPrice)}
                                </div>
                                <small className="text-success">
                                  {domainPricing.discountReason}
                                </small>
                              </div>
                            ) : (
                              <span className="fw-bold">{formatIDR(domainPricing.finalPrice)}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </Col>
                </Row>

                {/* Total Pembayaran Section - Moved below main content */}
                <div className="mt-4">
                  <hr className="my-3" />
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0">Total Pembayaran:</h6>
                      <small className="text-muted">
                        * Harga belum termasuk PPN
                      </small>
                    </div>
                    <span className="fw-bold text-warning">{(() => {
                      // Calculate totals
                        const currentServicePrice = serviceid && currentService && currentService.can_renew ?
                          parseFloat(currentService.selectedPrice || currentService.amount || currentService.renew_price || 0) : 0;

                      const hostingTotal = selectedHostingServices.reduce((sum, service) => {
                        return sum + parseFloat(service.selectedPrice || service.amount || service.renew_price || 0);
                      }, 0);

                      const domainTotal = selectedDomainServices.reduce((sum, domain) => {
                        const domainPricing = (() => {
                          const isBundleHostingDomain = domain.isBundleHostingDomain === true;

                          if (isBundleHostingDomain) {
                            const isRelatedHostingSelected = hostingServices.some(hosting =>
                              hosting.domain === domain.domain &&
                              selectedHostingServices.some(selected => selected.serviceid === hosting.serviceid)
                            );

                            if (isRelatedHostingSelected) {
                              const selectedPeriod = domain.selectedPeriod || domain.regperiod || 1;
                              const freePeriods = Math.min(domain.freePeriods || 1, 1);

                              if (selectedPeriod <= freePeriods) {
                                return 0; // Free
                              } else {
                                if (domain.selectedPeriod) {
                                  const selectedOption = domain.pricing_options.find(opt => opt.period == domain.selectedPeriod);
                                  return selectedOption ? parseFloat(selectedOption.amount) : 0;
                                } else {
                                  const currentPeriodOption = domain.pricing_options.find(opt => opt.period == (domain.regperiod || 1));
                                  return currentPeriodOption ? parseFloat(currentPeriodOption.amount) : 0;
                                }
                              }
                            } else {
                              if (domain.selectedPeriod) {
                                const selectedOption = domain.pricing_options.find(opt => opt.period == domain.selectedPeriod);
                                return selectedOption ? parseFloat(selectedOption.amount) : 0;
                              } else {
                                return domain.pricing_options[0] ? parseFloat(domain.pricing_options[0].amount) : 0;
                              }
                            }
                          } else {
                            return parseFloat(domain.selectedPrice || domain.amount || domain.renew_price || 0);
                          }
                        })();

                        return sum + domainPricing;
                      }, 0);

                      const finalTotal = currentServicePrice + hostingTotal + domainTotal;
                      return formatIDR(finalTotal);
                    })()}</span>
                  </div>

                  <div className="mt-4 text-center">
                    <AddToCartButton onClick={handleAddToCart} />
                  </div>
                </div>
              </CardWrapper>
            )}
          </div>
        </div>
      </div>
    </MainLayouts>
  );
};

export default Renew;
